import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";

/**
 * @typedef {object} T_HSLRotatorSetupResult
 * @property {number} hueAngleDeg
 * @property {number} saturatePercent
 * @property {number} lightlessPercent
 */

export default class HSLRotatorSetup extends Dialog {
	constructor() {
		super({
			title: i18n('title.hsl-rotate'),
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(res:T_HSLRotatorSetupResult|null)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * @returns {Promise<T_HSLRotatorSetupResult|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._onComplete) {
			this._onComplete({
				hueAngleDeg: parseInt(data.hueAngleDeg),
				saturatePercent: parseInt(data.saturatePercent),
				lightlessPercent: parseInt(data.lightlessPercent),
			});
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {hueAngleDeg: 0, saturatePercent: 100, lightlessPercent: 100},
			fields: [
				{
					type: 'angle360',
					name: 'hueAngleDeg',
					min: 0,
					max: 360,
					step: 1,
					caption: i18n('inputs.layer.hue'),
				},
				{
					type: 'range',
					name: 'saturatePercent',
					min: 0,
					max: 100,
					step: 1,
					caption: i18n('inputs.layer.saturation'),
				},
				{
					type: 'range200',
					name: 'lightlessPercent',
					min: 0,
					max: 200,
					step: 1,
					caption: i18n('inputs.layer.lightness'),
				},
			]
		});
		this.setContent(el);
	}

	/**
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
